import { useCallback, useEffect, useMemo } from 'react';
import { Col, Row, Form } from 'antd';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, ExcelIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import {
  CustomerServiceOutlined,
  EyeOutlined,
  FileImageOutlined, FileOutlined,
  FilePdfOutlined, PlaySquareOutlined,
} from '@ant-design/icons';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters, getUser } from 'hooks/useUser';
import BoardDocsApi from 'libs/api/boardDocs';
import BoardDocsAdapter from './boardDocs.adapter';
import { useApi } from 'hooks/useApi';
import moment from 'moment';
import axios from 'axios';
import AuthService from '../../../../services/Auth';
import FileTypeButton from '../../../../components/fileTypeButton';
import { fileTypes } from '../../../retailAcademy/subPages/academyArticles/useAcademyArticles';

const api = new BoardDocsApi();

const useBoardDocs = (isMainPage = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id, category } = useParams();
  const [form] = Form.useForm();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const user = useRecoilValue(getUser);
  const boardDocCategories = useMemo(
    () => [
      {
        id: 'weekly',
        label: t('Settimanali'),
      },
      {
        id: 'monthly',
        label: t('Mensili'),
      },
      {
        id: 'circulars',
        label: t('Circolari'),
      },
      {
        id: 'manuals',
        label: t('Manuali village'),
      },
      {
        id: 'admin_releases',
        label: t('Comunicati amministrativi'),
      },
    ],
    [t],
  );
  const aditionalFilters = useMemo(
    () => ({
      category,
    }),
    [category],
  );
  const {
    singleItem,
    data,
    loading,
    updateInProcess,
    onDelete,
    onChangeTableFilter,
    setUpdateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'boarddocs',
    singleResourceKey: 'boarddoc',
    api,
    adapter: BoardDocsAdapter,
    isMainPage,
    aditionalFilters,
  });
  const removeDiacritics = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  const handleDownload = async (url, fileName = 'vacancy_application.pdf', title = '') => {
    setUpdateInProcess(true);
    try {
      console.log('cur_filemame', fileName);
      const currentActiveItem = document.querySelector('.ant-menu-item-selected').textContent;
  
      let fileTitle = ''; // Initialize the variable outside the loop
      let fileFormat = '';
      for (const item of data.boarddocs) {
        if (item.file_name === fileName) {
          fileTitle = item.title;
        }
      }
      if (fileTitle === '') { 
        fileFormat = fileName.split('.').pop();
        console.log('fileFormat', fileFormat);
        fileTitle = `${removeDiacritics(title).replace(/\s+/g, '_').toLowerCase()}`;
      }
      let fileNameBase = '';
  
      // Handle currentActiveItem
      if (currentActiveItem.trim().split(/\s+/).length > 1) {
        fileNameBase += `${removeDiacritics(currentActiveItem).replace(/\s+/g, '_').toLowerCase()}`;
      } else {
        fileNameBase += `${removeDiacritics(currentActiveItem).toLowerCase()}`;
      }
      
      // Handle fileTitle
      if (fileTitle.trim().split(/\s+/).length > 1) {
        fileNameBase += `_${removeDiacritics(fileTitle).replace(/\s+/g, '_').toLowerCase()}`;
      } else {
        fileNameBase += `_${removeDiacritics(fileTitle).toLowerCase()}`;
      }
      
      fileName = `${fileNameBase}.${fileFormat || 'pdf'}`;
  
      const response = await api.getPDF(url);
      console.log('response=', response);
      Helpers.downloadFile(response, fileName);
    } catch (e) {
      console.log(e);
      UIHelper.serverError();
    } finally {
      setUpdateInProcess(false);
    }
  };
  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);

    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key === 'file') {
        if (values[key]) {
          formData.append(`boarddoc[${key}]`, values[key]?.originFileObj);
        }
        console.log(values[key]);
      } else {
        formData.append(`boarddoc[${key}]`, values[key]);
      }
    });
    try {
      if (!id) {
        await api.create({
          data: formData,
          headers: { 'Content-Type': 'multipart/Form-data' },
        });
        navigate(UIHelper.getListRoute(pathname) + '/circulars?by_shopping_center=&by_title=&category=&dateRange=');
      } else {
        await api.put(id, {
          data: formData,
          headers: { 'Content-Type': 'multipart/Form-data' },
        });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      console.log('e=', e);
      const errors = e.response.data || [];
      console.log('errors=', errors, ErrorHelper.getErrorsForForm(errors));
      form.setFields(ErrorHelper.getErrorsForForm(errors));
    } finally {
      setUpdateInProcess(false);
    }
  };
  const handleDownloadItemLogsExcel = useCallback(
    async (id) => {
      setUpdateInProcess(true);
      try {
        const currentActiveItem = document.querySelector('.ant-menu-item-selected').textContent;
        let fileTitle = '';
        if (currentActiveItem.trim().split(/\s+/).length > 1) {
          fileTitle += `${removeDiacritics(currentActiveItem).replace(/\s+/g, '_').toLowerCase()}`;
        } else {
          fileTitle += `${removeDiacritics(currentActiveItem).toLowerCase()}`;
        }
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (data.sorting.order) {
          params.sortable_column = data.sorting.field;
          params.direction = data.sorting.order;
        }
        const response = await api.getItemLogsExcel(id, { params });
        console.log('responce=', response);
        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate()}_${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}_${currentDate.getFullYear().toString().padStart(2, '0')}`;
        Helpers.downloadFile(response, `${fileTitle}_${formattedDate}.xlsx`);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.errors || [t('Something went wrong!')];
        UIHelper.openNotificationWithIcon('error', error.join('\n'));
      } finally {
        setUpdateInProcess(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, data.sorting, data.pagination, id],
  );

  const onClickFile = useCallback(
    async (e, url, name) => {
      e.preventDefault();
      try {
        await handleDownload(url, name);
      } catch (e) {}
      return;
      try {
        const {
          data: { file_url },
        } = await axios.get(url, {
          headers: {
            'auth-token': AuthService.getToken(),
          },
        });
        if (file_url) {
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          a.href = file_url;
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
        }
      } catch (e) {}
    },
    [handleDownload],
  );
  const columns = useMemo(() => {
    return [
      {
        title: t('Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Data Pubblicazione'),
        dataIndex: 'date',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: t('File'),
        dataIndex: 'file_format',
        sorter: () => {},
        render: (data, row) => {
          if (!data) return '';
          let icon = <FileOutlined />;
          if (data === fileTypes.pdf || data === fileTypes.image) {
            icon = data === fileTypes.pdf ? <FilePdfOutlined /> : <FileOutlined />;
            return (
              <Button
                onClick={(e) => onClickFile(e, row.file_url, row.file_name)}
                type={'green'}
                icon={icon}
              />
            );
          } else if (data === fileTypes.video) {
            icon = <PlaySquareOutlined />;
          } else if (data === fileTypes.music) {
            icon = <CustomerServiceOutlined />;
          }

          return (
            <Link to={`${pathname}/${row.id}/asset-view`}>
              <Button type={'green'} icon={icon} />
            </Link>
          );
        },
        // render: (data, row) => {
        //   const hasFile = !!row.file_url;
        //   return hasFile ? (
        //     <a
        //       href={row.file_url}
        //       target='_blank'
        //       rel='noopener noreferrer'
        //       onClick={(e) => onClickFile(e, row.file_url, row.file_name)}
        //     >
        //       <FileTypeButton row={row} data={data} handleDownloadPdf={handleDownload} />
        //       {/*{row.file_format === 'IMAGE' && (*/}
        //       {/*  <FileImageOutlined width={'100px'} height={'100px'} />*/}
        //       {/*)}*/}
        //       {/*{row.file_format === 'PDF' && <FilePdfOutlined />}*/}
        //       {/*{row.file_format === 'MP3' && <FilePdfOutlined />}*/}
        //     </a>
        //   ) : null;
        // },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 340,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            {!!user?.master_admin && (
              <>
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button icon={<EyeOutlined />} type={'primary'}>
                      {t('APRI')}
                    </Button>
                  </Link>
                </Col>
                <Col>
                  <Link to={`${pathname}/${row.id}/logs`}>
                    <Button type={'primary-white'}>{t('Logs')}</Button>
                  </Link>
                </Col>
                <Col>
                  {/*<Link to={`${pathname}/${row.id}/edit`}>*/}
                  <Button
                    type={'green'}
                    icon={<ExcelIcon fill='#fff' />}
                    onClick={() => handleDownloadItemLogsExcel(row.id)}
                  />
                  {/*</Link>*/}
                </Col>
              </>
            )}
            {row.can_update !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [pathname, t, handleDownloadItemLogsExcel, onClickFile, user]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        isActiveSearch: true,
      },
      {
        title: t('Categorie'),
        type: 'select',
        name: 'category',
        values: boardDocCategories.map((i) => ({
          value: i.id,
          title: i.label,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        keys: ['by_day_from', 'by_day_to'],
      },
    ],
    [t, shoppingCenters, boardDocCategories],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem?.title) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
        {
          name: 'date',
          value: singleItem.date && moment(singleItem.date),
        },
        {
          name: 'file',
          value: singleItem.file_present,
        },
        {
          name: 'mall_title',
          value: singleItem.shopping_center?.title,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  return {
    columns,
    data,
    filters,
    form,
    updateInProcess,
    handleCreateOrUpdate,
    handleDownloadExcel,
    onChangeTableFilter,
    shoppingCenters,
    loading,
    onDelete,
    singleItem,
    handleDownload,
  };
};

export default useBoardDocs;
